// src/lib/api_social_publish.ts
// Cliente para el módulo Social Publish (FB / IG) del backend Django.
// Mirrors apps/social_publish/ routers — /admin/posts/**.
//
// Reuses the /django-api proxy + JWT pattern from api_travel.ts.

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(extra: Record<string, string> = {}): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
    ...extra,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const url = `${DJANGO_PROXY}${path}`;
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers || {}) } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    // Ninja devuelve `detail` como lista de errores de validación en 422.
    const raw = err.detail || `Django ${res.status}: ${res.statusText}`;
    const detail = typeof raw === 'string'
      ? raw
      : Array.isArray(raw)
      ? raw.map((d: any) => (d?.loc ? `${(d.loc as unknown[]).slice(-1)[0]}: ` : '') + (d?.msg || JSON.stringify(d))).join('; ')
      : JSON.stringify(raw);
    if (res.status === 401) throw new ApiError('Sesión expirada. Inicia sesión nuevamente.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos para esta acción.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado.', 404);
    if (res.status === 413) throw new ApiError(detail || 'Archivo demasiado grande (máx 100 MB).', 413);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación. Revisa los campos.', 422);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ═══════════════════════════════════════════════════════════════
// Types — mirror apps/social_publish/schemas.py
// ═══════════════════════════════════════════════════════════════

export interface Paginated<T> { items: T[]; count: number }

export type Platform = 'facebook' | 'instagram';
export type PostType = 'feed' | 'story' | 'reel';
export type PostStatus = 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed';
export type MediaKind = 'image' | 'video';

export interface PostMedia {
  id: number;
  post_id: number;
  kind: MediaKind;
  media_url: string;
  thumbnail_url: string;
  alt_text: string;
  sort_order: number;
  created_at: string;
}

export interface ScheduledPost {
  id: number;
  org_id: string;
  platform: Platform;
  post_type: PostType;
  caption: string;
  hashtags: string[];
  scheduled_at: string | null;
  published_at: string | null;
  status: PostStatus;
  meta_post_id: string;
  error_message: string;
  ai_generated: boolean;
  ai_scene_description: string;
  context_data: Record<string, unknown>;
  video_generation_provider: string;
  has_audio: boolean;
  has_narration: boolean;
  impressions: number;
  reach: number;
  likes: number;
  comments_count: number;
  shares: number;
  saves: number;
  engagement_rate: number | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduledPostDetail extends ScheduledPost {
  media: PostMedia[];
}

export interface ScheduledPostIn {
  business_id?: string | null;
  platform?: Platform;
  post_type?: PostType;
  caption?: string;
  hashtags?: string[];
  scheduled_at?: string | null;
  ai_generated?: boolean;
  ai_scene_description?: string;
  context_data?: Record<string, unknown>;
}

export interface ScheduledPostUpdateIn {
  platform?: Platform;
  post_type?: PostType;
  caption?: string;
  hashtags?: string[];
  scheduled_at?: string | null;
  status?: PostStatus;
}

export interface PostMediaIn {
  kind?: MediaKind;
  media_url: string;
  thumbnail_url?: string;
  alt_text?: string;
  sort_order?: number;
}

export interface CalendarDay {
  date: string;               // YYYY-MM-DD
  posts: ScheduledPost[];
}

export interface PostActionOut {
  post_id: number;
  status: string;
  scheduled_at: string | null;
  detail?: string;
}

export interface MediaUploadOut {
  kind: MediaKind;
  media_url: string;
  storage_key: string;
  content_type: string;
  size_bytes: number;
  original_filename: string;
}

export interface ListPostsFilters {
  business_id?: string;
  status?: PostStatus;
  platform?: Platform;
  post_type?: PostType;
  ai_generated?: boolean;
  start_date?: string;
  end_date?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

// ═══════════════════════════════════════════════════════════════
// Query-string helper
// ═══════════════════════════════════════════════════════════════

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ═══════════════════════════════════════════════════════════════
// Posts CRUD
// ═══════════════════════════════════════════════════════════════

export async function listPosts(
  filters: ListPostsFilters = {},
): Promise<Paginated<ScheduledPost>> {
  const { limit = 25, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<ScheduledPost>>(
    `/api/v1/admin/posts/${qs({ ...rest, limit, offset })}`,
  );
}

export async function getPost(id: number): Promise<ScheduledPostDetail> {
  return djangoFetch<ScheduledPostDetail>(`/api/v1/admin/posts/${id}`);
}

export async function createPost(data: ScheduledPostIn): Promise<ScheduledPostDetail> {
  return djangoFetch<ScheduledPostDetail>('/api/v1/admin/posts/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePost(
  id: number,
  data: ScheduledPostUpdateIn,
): Promise<ScheduledPost> {
  return djangoFetch<ScheduledPost>(`/api/v1/admin/posts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePost(id: number): Promise<void> {
  await djangoFetch<void>(`/api/v1/admin/posts/${id}`, { method: 'DELETE' });
}

// ═══════════════════════════════════════════════════════════════
// Calendar view
// ═══════════════════════════════════════════════════════════════

export async function getCalendar(
  params: { start_date?: string; end_date?: string; business_id?: string } = {},
): Promise<CalendarDay[]> {
  return djangoFetch<CalendarDay[]>(
    `/api/v1/admin/posts/calendar${qs(params)}`,
  );
}

// ═══════════════════════════════════════════════════════════════
// Publish-now + nested media
// ═══════════════════════════════════════════════════════════════

export async function publishNow(
  id: number,
  platform?: Platform,
): Promise<PostActionOut> {
  return djangoFetch<PostActionOut>(`/api/v1/admin/posts/${id}/publish-now`, {
    method: 'POST',
    body: JSON.stringify(platform ? { platform } : {}),
  });
}

export async function attachMedia(
  postId: number,
  data: PostMediaIn,
): Promise<PostMedia> {
  return djangoFetch<PostMedia>(`/api/v1/admin/posts/${postId}/media`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function removeMedia(postId: number, mediaId: number): Promise<void> {
  await djangoFetch<void>(`/api/v1/admin/posts/${postId}/media/${mediaId}`, {
    method: 'DELETE',
  });
}

// ═══════════════════════════════════════════════════════════════
// Multipart upload
// ═══════════════════════════════════════════════════════════════

/** Uploads a single file to the backend which stores it on Bunny and
 *  returns the CDN URL + kind (image/video). El opcional `onProgress` recibe
 *  un valor 0..1 mientras los bytes salen del browser — fetch() no expone
 *  upload progress, asi que cuando hace falta visibilidad usamos XHR. */
export async function uploadMedia(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<MediaUploadOut> {
  const form = new FormData();
  form.append('file', file);
  const token = await getJwt();
  const url = `${DJANGO_PROXY}/api/v1/admin/posts/upload-media`;

  return new Promise<MediaUploadOut>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.responseType = 'json';

    if (onProgress) {
      xhr.upload.onprogress = (ev: ProgressEvent) => {
        if (ev.lengthComputable) {
          onProgress(ev.loaded / ev.total);
        }
      };
    }

    xhr.onload = () => {
      const status = xhr.status;
      const body = xhr.response;
      if (status >= 200 && status < 300) {
        resolve(body as MediaUploadOut);
        return;
      }
      const detail =
        (body && typeof body === 'object' && (body as any).detail) ||
        `Upload ${status}: ${xhr.statusText}`;
      if (status === 413) {
        reject(new ApiError('Archivo demasiado grande (máx 250 MB).', 413));
      } else if (status === 422) {
        reject(new ApiError(typeof detail === 'string' ? detail : 'Tipo de archivo no soportado.', 422));
      } else {
        reject(new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), status));
      }
    };
    xhr.onerror = () => reject(new ApiError('Conexión perdida durante la subida.', 0));
    xhr.ontimeout = () => reject(new ApiError('Timeout en la subida.', 0));
    xhr.send(form);
  });
}
